<script setup>
import { computed } from 'vue';

const props = defineProps({
  stages: {
    type: Array,
    default: () => [],
  },
  currentStage: {
    type: String,
    default: '',
  },
  canMove: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits(['moveToStage']);

const currentIndex = computed(() =>
  props.stages.findIndex(stage => stage.name === props.currentStage)
);

const stageState = index => {
  if (index === currentIndex.value) return 'now';
  if (currentIndex.value === -1) return 'future';
  return index < currentIndex.value ? 'done' : 'future';
};

const stageClasses = index => {
  const state = stageState(index);
  if (state === 'now') return 'now bg-n-brand text-white';
  if (state === 'done') return 'text-n-blue-11 bg-n-blue-3';
  return 'text-n-slate-10 bg-n-solid-2';
};

const NOTCH = '0.625rem';

const clipPath = index => {
  const isFirst = index === 0;
  const isLast = index === props.stages.length - 1;

  if (isFirst && isLast) return 'none';
  if (isFirst) {
    return `polygon(0 0, calc(100% - ${NOTCH}) 0, 100% 50%, calc(100% - ${NOTCH}) 100%, 0 100%)`;
  }
  if (isLast) {
    return `polygon(${NOTCH} 0, 100% 0, 100% 100%, ${NOTCH} 100%, 0 50%)`;
  }
  return `polygon(${NOTCH} 0, calc(100% - ${NOTCH}) 0, 100% 50%, calc(100% - ${NOTCH}) 100%, ${NOTCH} 100%, 0 50%)`;
};

const handleClick = stage => {
  if (!props.canMove) return;
  if (stage.name === props.currentStage) return;
  emit('moveToStage', stage.name);
};
</script>

<template>
  <div class="flex items-center overflow-x-auto no-scrollbar">
    <button
      v-for="(stage, index) in stages"
      :key="stage.name"
      type="button"
      :data-stage="stage.name"
      class="relative flex items-center justify-center h-7 px-3 -ml-2 first:ml-0 text-[11px] font-medium whitespace-nowrap shrink-0 transition-colors"
      :class="[
        stageClasses(index),
        canMove && stage.name !== currentStage
          ? 'cursor-pointer hover:opacity-90'
          : 'cursor-default',
      ]"
      :style="{ clipPath: clipPath(index) }"
      @click="handleClick(stage)"
    >
      <span>{{ stage.label || stage.name }}</span>
    </button>
  </div>
</template>
